import { createBrowserRouter, Navigate } from 'react-router-dom';

// Local Unify-only utility pages — the dashboard intentionally does NOT use
// `@pages/*` from `dashboard-shared` because those modules import
// `@nearst/ui`, which side-effect-loads a global SCSS that conflicts with
// the Unify design tokens (e.g. swapping the system-ui font for Poppins).
import { Login, LoginSsoCallback } from '@/pages/login';
import { Logout } from '@/pages/logout';
import { Impersonate } from '@/pages/impersonate';
import { NotFound } from '@/pages/errors/NotFound';

// existing onboarding (re-skinned in NEA-5673; for now wired into the new shell)
import { UnifyOnboarding } from './pages/onboarding';

// new Unify dashboard pages — see Unify: Dashboard project
import { ShopRoutingGate, ShopScopedLayout } from '@/components/layout/ShopRouting';
import { PrivateRoute } from '@/components/auth/PrivateRoute';
import { WelcomeGate } from '@/components/auth/WelcomeGate';
import { Welcome } from '@/pages/welcome';
import { LocalCheckoutReturnRedirect } from '@/pages/channels/setup/LocalCheckoutSetup';
import { DesignGuidelines } from '@/pages/design-guidelines';

// The shop-scoped page tree lives in `app-routes.jsx` so it can carry the
// per-page metadata (`handle`) the AI assistant's dashboard map derives from.
import { appChildren } from '@/app-routes';

const SignUpRedirect = () => <Navigate to="/onboarding" />;

export const router = createBrowserRouter(
	[
		{ path: '/login', element: <Login /> },
		{ path: '/login/sso-callback', element: <LoginSsoCallback /> },
		{ path: '/sign-up', element: <SignUpRedirect /> },
		{ path: '/impersonate', element: <Impersonate /> },
		// Component preview lives outside the AppLayout — dev-only design comparison surface.
		{ path: '/design-guidelines', element: <DesignGuidelines /> },
		{ path: '/logout/:shopId?', element: <Logout /> },
		// First-login welcome screen — its own minimal layout, outside the
		// shell. Authed (so we know who the user is) but intentionally NOT
		// wrapped in `WelcomeGate`: the page self-protects, redirecting
		// already-dismissed / impersonating users to the dashboard root.
		{
			path: '/welcome',
			element: (
				<PrivateRoute>
					<Welcome />
				</PrivateRoute>
			)
		},
		// Bridge for Stripe Connect return URLs. The shared backend handler
		// (`api-handlers/src/handlers/checkout/create-account.js`) hardcodes
		// the return path as `/channels/{shopId}/settings/local-checkout`,
		// which doesn't match unify's `/channels/{id}/setup` shape. Catch
		// the legacy URL here and forward.
		{
			path: '/channels/:legacyShopId/settings/local-checkout',
			element: (
				<PrivateRoute>
					<LocalCheckoutReturnRedirect />
				</PrivateRoute>
			)
		},
		// Single-shop / global onboarding — full-screen, no shell (unchanged).
		// Public, no PrivateRoute: this is the registration entry, so a
		// logged-out new user must be able to reach it to sign up — the flow
		// drives Clerk `signUp.create()` itself, and `/login`'s widget points
		// its "create account" link here (NEA-5767). Multi-shop onboarding is
		// the authed `/:shopId/onboarding` child below, which renders inside
		// the shell so the shop switcher stays reachable.
		{
			path: '/onboarding',
			element: <UnifyOnboarding />
		},
		{
			path: '/',
			element: (
				<PrivateRoute>
					<WelcomeGate>
						<ShopRoutingGate />
					</WelcomeGate>
				</PrivateRoute>
			),
			children: appChildren
		},
		{
			path: '/:shopId',
			element: (
				<PrivateRoute>
					<WelcomeGate>
						<ShopScopedLayout />
					</WelcomeGate>
				</PrivateRoute>
			),
			children: [...appChildren, { path: 'onboarding', element: <UnifyOnboarding /> }]
		},
		{ path: '*', element: <NotFound /> }
	],
	{
		future: {
			v7_skipActionStatusRevalidation: true,
			v7_fetcherPersist: true,
			v7_startTransition: true,
			v7_normalizeFormMethod: true,
			v7_partialHydration: true
		}
	}
);
